import { MediaContentDTO } from './Image'
import { Brand } from './Brand'
import { Person } from './Person'
import { Product } from './Product'
import moment, { Moment } from 'moment'

export enum GenreTypes {
  ACTION = 'Action',
  ADVENTURE = 'Adventure',
  ANIMATION = 'Animation',
  BIOGRAPHY = 'Biography',
  CRIME = 'Crime',
  COMEDY = 'Comedy',
  DOCUMENTARY = 'Documentary',
  DRAMA = 'Drama',
  FAMILY = 'Family',
  FANTASY = 'Fantasy',
  FILM_NOIR = 'Film - Noir',
  HISTORY = 'History',
  HORROR = 'Horror',
  MUSIC = 'Music',
  MYSTERY = 'Mystery',
  ROMANCE = 'Romance',
  SCI_FI = 'Sci - Fi',
  SPORT = 'Sport',
  THRILLER = 'Thriller',
  WAR = 'War',
  WESTERN = 'Western',
}
export enum ApiProjectTypes {
  MOVIE = 'Movie',
  TV_SERIES = 'TV Series',
  VOV = 'VoV',
  SPORT = 'Sport'
}
export enum ApiSeriesTypes {
  TV_SEASON = 'TV Season',
  TV_EPISODE = 'TV Episode'
}
export const ProjectTypes = { ...ApiProjectTypes, ...ApiSeriesTypes }
export type ProjectTypes = ApiProjectTypes & ApiSeriesTypes
export enum ParamProjectTypes {
  MOVIES = 'movies',
  TV_SERIES = 'tv-series',
  VOV = 'vov'
}
export enum ApiVovSubtypes {
  BLOOPER = 'Blooper',
  BEHIND_THE_SCENES = 'Behind The Scenes',
  DELETED_SCENES = 'Deleted Scenes',
  AUDITION = 'Audition',
  REHEARSAL = 'Rehearsal',
  THE_MAKING_OF = 'The Making of',
  TRAILER = 'Trailer',
  DIRECTORS_CUT = 'Director\'s Cut',
  CLIP = 'Clip'
}
export enum StatusTypes {
  NEW = 'NEW',
  NOT_PUBLISHED = 'NOT PUBLISHED',
  PUBLISHED = 'PUBLISHED',
  HAS_CHANGES = 'HAS CHANGED'
}

export enum autoObjectDetectionTypes {
  NOT_STARTED = 'NOT_STARTED',
  PROCESSING = 'PROCESSING',
  FRAME_STORE_START = 'FRAME_STORE_START',
  FRAME_STORE_COMPLETE = 'FRAME_STORE_COMPLETE',
  GEMINI_START = 'GEMINI_START',
  GEMINI_COMPLETE = 'GEMINI_COMPLETE',
  OPENCV_START = 'OPENCV_START',
  OPENCV_COMPLETE = 'OPENCV_COMPLETE',
  COMPLETED = 'COMPLETED'
}

export interface ProjectDTO {
  id: number
  externalId: string
  parentId: number
  parentExternalId: string
  videoEntityId: number
  ownerId: string

  type: ProjectTypes
  subtype: ApiVovSubtypes
  status: StatusTypes

  name: string
  genres: GenreTypes[]
  year: number
  tags: string
  info: string
  images: MediaContentDTO[]
  mainImage: MediaContentDTO
  marketplaceType:string
  number: number

  videoUrl: string
  startTime: number
  duration: number
  frames: number
  height: number
  width: number
  urlSpriteS: string
  urlSpriteL: string
  urlSpriteG: string

  brands: Brand[]
  products: Product[]
  actors: Person[]
  directors: Person[]
  producers: Person[]
  guestActors: Person[]

  relatedMedia: ProjectDTO[]
  children: ProjectDTO[]

  createdDate: number
  loadContents: boolean
  loadHierarchy: boolean
  mergeWithMarketplace: boolean
  readyForEdit: boolean
  autoGroupAccuracy?: number
  autoObjectDetection?:autoObjectDetectionTypes
  inputGeminiTokens?: number
  outputGeminiTokens?: number
}

export interface ProjectDTONameAndId {
  id: number
  name: string
}

export class Project {
  id: number
  externalId: string
  parentId: number
  parentExternalId: string
  videoEntityId: number
  ownerId: string

  type: ProjectTypes
  subtype: ApiVovSubtypes
  status: StatusTypes

  name: string
  genres: GenreTypes[]
  year: number
  tags: string
  info: string
  images: MediaContentDTO[]
  mainImage: MediaContentDTO
  marketplaceType:string
  number: number

  videoUrl: string
  startTime: number
  duration: number
  frames: number
  height: number
  width: number
  urlSpriteS: string
  urlSpriteL: string
  urlSpriteG: string

  brands: Brand[]
  products: Product[]
  actors: Person[]
  directors: Person[]
  producers: Person[]
  guestActors: Person[]

  relatedMedia: Project[]
  children: Project[]

  createdDate: Moment
  loadContents: boolean
  loadHierarchy: boolean
  mergeWithMarketplace: boolean
  readyForEdit: boolean
  autoGroupAccuracy?: number
  autoObjectDetection?:autoObjectDetectionTypes
  inputGeminiTokens?: number
  outputGeminiTokens?: number

  constructor (project: ProjectDTO) {
    for (const property in project) {
      switch (property) {
        case 'relatedMedia':
        case 'children': {
          this[property] = (project[property] || []).map(p => new Project(p))
          break
        }
        case 'createdDate': {
          this[property] = moment(project[property])
          break
        }
        default: {
          this[property] = project[property]
          break
        }
      }
    }
  }

  get genresAsString () {
    return (this.genres || []).join(', ')
  }

  get addedAtAsString () {
    return moment(this.createdDate).format('DD.MM.YYYY')
  }

  get tagsAsArray () {
    return this.tags
      ? this.tags.split(',')
      : []
  }

  get asDTO (): ProjectDTO {
    return {
      actors: this.actors,
      brands: this.brands,
      children: (this.children || []).map(p => p.asDTO),
      createdDate: this.createdDate.valueOf(),
      directors: this.directors,
      duration: this.duration,
      externalId: this.externalId,
      frames: this.frames,
      genres: this.genres,
      guestActors: this.guestActors,
      height: this.height,
      id: this.id,
      images: this.images,
      info: this.info,
      loadContents: this.loadContents,
      loadHierarchy: this.loadHierarchy,
      mainImage: this.mainImage,
      marketplaceType: this.marketplaceType,
      mergeWithMarketplace: this.mergeWithMarketplace,
      name: this.name,
      number: this.number,
      ownerId: this.ownerId,
      parentExternalId: this.parentExternalId,
      parentId: this.parentId,
      producers: this.producers,
      products: this.products,
      readyForEdit: this.readyForEdit,
      relatedMedia: (this.relatedMedia || []).map(p => p.asDTO),
      startTime: this.startTime,
      status: this.status,
      subtype: this.subtype,
      tags: this.tags,
      type: this.type,
      urlSpriteG: this.urlSpriteG,
      urlSpriteL: this.urlSpriteL,
      urlSpriteS: this.urlSpriteS,
      videoEntityId: this.videoEntityId,
      videoUrl: this.videoUrl,
      width: this.width,
      year: this.year,
      autoGroupAccuracy: this.autoGroupAccuracy,
      autoObjectDetection: this.autoObjectDetection,
      inputGeminiTokens: this.inputGeminiTokens,
      outputGeminiTokens: this.outputGeminiTokens
    }
  }
}
